/**
 * ValidatorTemplate.tsx — Phone vs IP Validator and E-mail Validator.
 * Covers: ipgeo_number (sub-types geolocation_number and email_domain_validator).
 *
 * Both are single-answer checks, so both get the same shape: one verdict, the
 * values that were compared, nothing else.
 *
 * HONESTY NOTE: the e-mail validator payload is literally `{ status: false }`.
 * The old UI printed "E-mail domain is not exists or fake", which the response
 * does not say. This template only reports what came back. See
 * INTEGRATION.md §Backend notes for the suggested `reason` field.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { prepareIpPhoneGeolocationResult } from '../vendor/searcherService'
import { Compare, NoData, RawCard, ResCard, Verdict, Warn, useTokens } from '../ui/primitives'
import type { TemplateProps } from '../registry'
import { radii } from '../tokens'

const tick = <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="m5 13 4 4L19 7" /></svg>
const cross = <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M18 6 6 18M6 6l12 12" /></svg>

function readEmailValidator(apiData: any): { status: boolean; reason?: string } | null {
  const entities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []
  for (const e of entities) {
    const v = e?.data?.email_validator
    if (v && typeof v === 'object') return { status: !!v.status, reason: v.reason || v.message }
  }
  return null
}

export function ValidatorTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()

  const phoneIp: any = React.useMemo(() => {
    try {
      const r = prepareIpPhoneGeolocationResult(apiData as any)
      const first = Array.isArray(r?.data) && r.data.length ? r.data[0] : null
      // service.ts's email_domain_validator branch pushes `{ status }` only
      // (no ip/phone at all — see prepareIpPhoneGeolocationResult's `else`),
      // so `first` was truthy for BOTH sub-types and this branch always won,
      // even for e-mail results. Only count it as phone-vs-IP data when it
      // actually carries an ip or phone field.
      return first && (first.ip !== undefined || first.phone !== undefined) ? first : null
    } catch {
      return null
    }
  }, [apiData])

  const email = React.useMemo(() => readEmailValidator(apiData), [apiData])

  /* ---------------- phone vs IP ---------------- */
  if (phoneIp) {
    const ok = phoneIp.status === true || String(phoneIp.status).toLowerCase() === 'true'
    return (
      <>
        <ResCard title="Verdict" tone={ok ? 'good' : 'bad'}>
          <Verdict
            tone={ok ? 'ok' : 'bad'}
            title={ok ? 'Same country' : 'Country mismatch'}
            subtitle={
              ok
                ? 'The phone and the IP address resolve to the same country'
                : 'The phone and the IP address resolve to different countries'
            }
            icon={ok ? tick : cross}
          />
          <Compare
            ok={ok}
            left={['Phone', phoneIp.phone ?? '—', [phoneIp.phoneCountry, phoneIp.phoneCountryCode].filter(Boolean).join(' · ')]}
            right={['IP address', phoneIp.ip ?? '—', [phoneIp.ipCountry, phoneIp.ipCountryCode].filter(Boolean).join(' · ')]}
          />
        </ResCard>
        <RawCard data={apiData} />
      </>
    )
  }

  /* ---------------- e-mail validator ---------------- */
  if (email) {
    const address = String((apiData as any)?.criteria ?? '')
    const domain = address.split('@')[1] || '—'
    return (
      <>
        <ResCard title="Verdict" tone={email.status ? 'good' : 'bad'}>
          <Verdict
            tone={email.status ? 'ok' : 'bad'}
            title={email.status ? 'Deliverable' : 'Not deliverable'}
            subtitle={address}
            icon={email.status ? tick : cross}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', mt: '14px', '@media (max-width:680px)': { gridTemplateColumns: '1fr' } }}>
            <Box sx={{ background: tk.surface2, border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, p: '12px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.07em', color: tk.text3, fontWeight: 700 }}>Address</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 17, mt: '3px', color: tk.text }}>{address || '—'}</Typography>
              <Typography sx={{ fontSize: 11, color: tk.text3 }}>domain {domain}</Typography>
            </Box>
            <Box sx={{ background: tk.surface2, border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, p: '12px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.07em', color: tk.text3, fontWeight: 700 }}>Verdict</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 17, mt: '3px', color: email.status ? '#10B981' : '#EF4444' }}>
                {email.status ? 'Valid' : 'Invalid'}
              </Typography>
              <Typography sx={{ fontSize: 11, color: tk.text3 }}>status: {String(email.status)}</Typography>
            </Box>
          </Box>

          {!email.reason && (
            <Box sx={{ mt: '13px' }}>
              <Warn title="The API returns no reason">
                The payload is only <code>{'{ status: false }'}</code>. Any explanation shown beyond “not deliverable”
                would be invented by the UI. Ask the backend for a <code>reason</code> field.
              </Warn>
            </Box>
          )}
          {email.reason && (
            <Typography sx={{ fontSize: 12.5, color: tk.text2, mt: '12px' }}>Reason: {email.reason}</Typography>
          )}
        </ResCard>
        <RawCard data={apiData} />
      </>
    )
  }

  return <NoData title="No validation result" hint="The validator did not return a verdict for this input." />
}
